import { ArrowRight, ArrowUp, TicketCheck } from "lucide-react";
import { FormEvent, useState } from "react";
import { DetailWindow } from "../DetailWindow";
import {
  type ConvertibleResource,
  type GrowthProfile,
  type RedeemResult,
  type ResourceConversionResult,
} from "../types";
import { currencyLabel, messageOf } from "../ui/format";
import { Resource, ResourceIcon, type ResourceKind, type RewardKind } from "../ui/Resources";
import { WorkshopIcon, WorkshopOperationSlot } from "./WorkshopOperation";

export function WorkshopScreen({
  growth,
  busy,
  onUpgrade,
  onConvert,
  onRedeem,
}: {
  growth: GrowthProfile;
  busy: boolean;
  onUpgrade: (buildingId: string) => void;
  onConvert: (
    sourceCurrency: ConvertibleResource,
    targetCurrency: ConvertibleResource,
    sourceAmount: number,
  ) => Promise<ResourceConversionResult>;
  onRedeem: (code: string) => Promise<RedeemResult>;
}) {
  const [selectedId, setSelectedId] = useState(() => growth.buildings[0]?.id ?? "");
  const [buildingWindowOpen, setBuildingWindowOpen] = useState(false);
  const [code, setCode] = useState("");
  const [redeemMessage, setRedeemMessage] = useState("");
  const [redeemResult, setRedeemResult] = useState<RedeemResult | null>(null);
  const selectedBuilding =
    growth.buildings.find((building) => building.id === selectedId) ?? growth.buildings[0];
  const maxed = selectedBuilding ? selectedBuilding.level >= selectedBuilding.maxLevel : false;
  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim() || busy) return;
    setRedeemMessage("");
    setRedeemResult(null);
    try {
      const result = await onRedeem(code);
      setRedeemResult(result);
      setRedeemMessage(result.replayed ? "奖励已领取，钱包已同步" : "兑换成功");
      setCode("");
    } catch (error) {
      setRedeemMessage(messageOf(error));
    }
  };
  return (
    <section
      className="growth-dialog growth-screen workshop-dialog"
      aria-labelledby="workshop-title"
    >
      <header className="growth-dialog-header">
        <div>
          <h1 id="workshop-title">工坊</h1>
        </div>
      </header>
      <div className="workshop-wallet">
        <Resource
          kind="wood"
          value={growth.balances.wood}
          limit={growth.effects.storageCaps.wood}
        />
        <Resource kind="ore" value={growth.balances.ore} limit={growth.effects.storageCaps.ore} />
        <Resource
          kind="gold"
          value={growth.balances.gold}
          limit={growth.effects.storageCaps.gold}
        />
        <Resource kind="crystal" value={growth.balances.crystal} limit={null} />
      </div>
      <div className="workshop-layout">
        <div className="workshop-roster" aria-label="工坊建筑">
          {growth.buildings.map((building) => (
            <button
              key={building.id}
              className={`workshop-building${buildingWindowOpen && building.id === selectedBuilding?.id ? " is-selected" : ""}`}
              aria-haspopup="dialog"
              onClick={() => {
                setSelectedId(building.id);
                setBuildingWindowOpen(true);
              }}
            >
              <span className="workshop-building-icon">
                <WorkshopIcon buildingId={building.id} />
              </span>
              <span className="workshop-building-copy">
                <strong>{building.name}</strong>
                <small>{building.effect}</small>
              </span>
              <b>Lv.{building.level}</b>
              <i>
                <em
                  style={{
                    width: `${building.maxLevel > 0 ? (building.level / building.maxLevel) * 100 : 0}%`,
                  }}
                />
              </i>
            </button>
          ))}
        </div>
        {selectedBuilding && buildingWindowOpen && (
          <DetailWindow
            className="workshop-window"
            labelledBy="workshop-window-title"
            onClose={() => setBuildingWindowOpen(false)}
            header={
              <h2 id="workshop-window-title">
                <WorkshopIcon buildingId={selectedBuilding.id} />
                {selectedBuilding.name}
              </h2>
            }
          >
            <section className="workshop-detail" aria-labelledby="workshop-building-title">
              <div className="workshop-detail-title">
                <span>
                  <WorkshopIcon buildingId={selectedBuilding.id} size={24} />
                </span>
                <div>
                  <strong id="workshop-building-title">{selectedBuilding.name}</strong>
                  <small>
                    Lv.{selectedBuilding.level} / {selectedBuilding.maxLevel}
                  </small>
                </div>
              </div>
              <div className="workshop-effect-compare">
                <span>
                  <small>当前</small>
                  <strong>{selectedBuilding.effect}</strong>
                </span>
                <ArrowRight size={16} />
                <span>
                  <small>下一级</small>
                  <strong>{maxed ? "已达到最高等级" : selectedBuilding.nextEffect}</strong>
                </span>
              </div>
              <div className="workshop-cost" aria-label="升级消耗">
                {maxed ? (
                  <span>已满级</span>
                ) : (
                  Object.entries(selectedBuilding.cost).map(([currency, amount]) => {
                    const enough = growth.balances[currency as ResourceKind] >= amount;
                    return (
                      <span
                        key={currency}
                        className={enough ? "" : "is-short"}
                        title={`${currencyLabel(currency)} ${amount}`}
                      >
                        <ResourceIcon kind={currency as ResourceKind} size={16} />
                        <small>{currencyLabel(currency)}</small>
                        <b>{amount}</b>
                      </span>
                    );
                  })
                )}
              </div>
              <button
                className="primary-command compact"
                title={maxed ? "已满级" : selectedBuilding.canUpgrade ? "升级" : "资源不足"}
                disabled={busy || maxed || !selectedBuilding.canUpgrade}
                onClick={() => onUpgrade(selectedBuilding.id)}
              >
                <ArrowUp size={18} />
                {maxed ? "已满级" : "升级"}
              </button>
            </section>
            <WorkshopOperationSlot
              building={selectedBuilding}
              growth={growth}
              busy={busy}
              onConvert={onConvert}
            />
          </DetailWindow>
        )}
      </div>
      <form className="redeem-strip" onSubmit={submitCode}>
        <span className="redeem-strip-icon">
          <TicketCheck size={21} />
        </span>
        <label htmlFor="redeem-code">
          <strong>兑换码</strong>
          <small>账号奖励</small>
        </label>
        <input
          id="redeem-code"
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase().slice(0, 32))}
          autoComplete="off"
          spellCheck={false}
          placeholder="BR-XXXX-XXXX-XXXX"
          aria-label="兑换码"
        />
        <button className="primary-command compact" type="submit" disabled={busy || !code.trim()}>
          <TicketCheck size={17} />
          兑换
        </button>
        {redeemMessage && (
          <div
            className={`redeem-status${redeemResult ? " is-success" : ""}`}
            role="status"
            aria-live="polite"
          >
            <span>{redeemMessage}</span>
            {redeemResult && (
              <div>
                {Object.entries(redeemResult.reward)
                  .filter(([, amount]) => amount > 0)
                  .map(([currency, amount]) => (
                    <b key={currency} title={currencyLabel(currency)}>
                      <ResourceIcon kind={currency as RewardKind} size={14} />+{amount}
                    </b>
                  ))}
              </div>
            )}
          </div>
        )}
      </form>
    </section>
  );
}
